import * as SelectPrimitive from '@radix-ui/react-select';
import { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api';
import { Icon } from './icons';

export const money = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: value && value < 1 ? 4 : 2 }).format(Number(value ?? 0));
export const shortNumber = (value) => new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(Number(value ?? 0));
export const date = (value) => value ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—';
export const dayOnly = (value) => value ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(new Date(value)) : '—';
export const duration = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
};
export const ago = (value) => {
  if (!value) return '—';
  const diff = Date.now() - new Date(value).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};
export const titleCase = (value) => String(value ?? '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
/** `<input type="date">` value → ISO at end of that day, or null when cleared. */
export const dateInputToIso = (value, endOfDay = true) => value ? new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00'}`).toISOString() : null;
export const isoToDateInput = (value) => value ? new Date(value).toISOString().slice(0, 10) : '';

/** Who is signed in, with their effective permissions — read anywhere with `useMe()`. */
export const MeContext = createContext(null);
export const useMe = () => useContext(MeContext);
export const useCan = () => { const me = useMe(); return (permission) => Boolean(me?.permissions?.includes(permission)); };

/** Active team members for owner pickers; fetched once per mount. */
export const useAssignees = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api('/team/assignees').then((r) => setItems(r.items ?? [])).catch(() => setItems([])); }, []);
  return items;
};

export function Badge({ children, tone = 'muted', className = '', title }) { return <span className={`badge ${tone} ${className}`} title={title}>{children ?? 'Not started'}</span>; }
export const toneFor = (status) =>
  ['done', 'succeeded', 'resolved', 'closed'].includes(status) ? 'good'
    : ['error', 'failed', 'blocked', 'cancelled'].includes(status) ? 'danger'
    : ['rendering', 'running', 'queued', 'in_progress', 'investigating', 'mitigating', 'monitoring', 'triaged'].includes(status) ? 'warn'
    : status === 'open' ? 'info' : 'muted';
/**
 * Which render backend produced an export. Two are in service — Remotion
 * Lambda on AWS and a Cloud Run Job on GCP — and a failure or a cost reads
 * differently depending on which one it came from. Rows older than the
 * column have no value; all of those were Lambda, and the label says so
 * with the hedge intact.
 */
const BACKEND_LABELS = { lambda: 'AWS Lambda', cloudrun: 'Cloud Run' };
export function BackendBadge({ backend }) {
  const label = BACKEND_LABELS[backend];
  return <Badge tone={backend === 'cloudrun' ? 'info' : 'muted'} className="backend-badge" title={label ? `Rendered on ${label}` : 'Recorded before the backend was tracked — Lambda'}>{label ?? 'Lambda (legacy)'}</Badge>;
}

export function StateBadge({ status }) { return <Badge tone={toneFor(status)}>{status ? titleCase(status) : 'not started'}</Badge>; }
export const severityTone = (severity) => severity === 'sev1' || severity === 'sev2' ? 'danger' : severity === 'sev3' ? 'warn' : 'muted';
export const priorityTone = (priority) => priority === 'urgent' ? 'danger' : priority === 'high' ? 'warn' : priority === 'low' ? 'muted' : 'info';

export function Metric({ label, value, detail, alert }) { return <article className={`metric ${alert ? 'attention' : ''}`}><span>{label}</span><strong>{value}</strong><small>{detail}</small></article>; }
export function Empty({ title, body, action }) { return <div className="empty"><div><Icon name="inbox" size={18} /></div><b>{title}</b><p>{body}</p>{action}</div>; }
export function Loading({ label = 'Loading operations data…' }) { return <div className="loading"><span />{label}</div>; }
export function ErrorState({ message, retry }) { return <div className="error-state"><b>Could not load this view.</b><p>{message}</p>{retry && <button className="subtle" onClick={retry}>Try again</button>}</div>; }
export function Avatar({ name, email, size = 30 }) { return <span className="avatar" style={{ width: size, height: size, fontSize: size * .42 }} title={email}>{(name || email || '?').slice(0, 1).toUpperCase()}</span>; }

/** Labelled form control; `children` is the input/select/textarea. */
export function Field({ label, hint, children, inline }) {
  return <label className={`field ${inline ? 'inline' : ''}`}><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

/**
 * Right-hand panel for detail views. Full-screen on narrow viewports. Closes
 * on Escape or backdrop click; the caller decides what "close" means.
 */
export function Drawer({ title, subtitle, onClose, children, footer, wide }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.classList.add('drawer-open');
    return () => { window.removeEventListener('keydown', onKey); document.body.classList.remove('drawer-open'); };
  }, [onClose]);
  return (
    <div className="drawer-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className={`drawer ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        <header className="drawer-head"><div>{subtitle && <span className="eyebrow">{subtitle}</span>}<h2>{title}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close"><Icon name="close" /></button></header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-foot">{footer}</footer>}
      </aside>
    </div>
  );
}

/**
 * Light/dark switch. public/theme-init.js resolves the theme before first
 * paint and owns the `data-theme` attribute; this only flips it and remembers
 * the choice. Which icon shows is decided in CSS from that attribute, so the
 * button never needs React state to stay in sync with the OS setting.
 */
const THEME_KEY = 'cm.admin.theme';
export function ThemeToggle() {
  const toggle = () => {
    const root = document.documentElement;
    const next = root.dataset.theme === 'dark' ? 'light' : 'dark';
    root.dataset.theme = next;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', next === 'dark' ? '#09090b' : '#fafafa');
    try { localStorage.setItem(THEME_KEY, next); } catch { /* private mode: applies for this visit only */ }
  };
  return (
    <button type="button" className="icon-button theme-toggle" onClick={toggle} aria-label="Toggle dark mode" title="Toggle dark mode">
      <Icon name="sun" className="theme-sun" /><Icon name="moon" className="theme-moon" />
    </button>
  );
}

/** Small transient toast for save confirmations and errors. */
export function useToast() {
  const [toast, setToast] = useState(null);
  useEffect(() => { if (!toast) return undefined; const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); }, [toast]);
  const show = (message, tone = 'good') => setToast({ message, tone });
  const node = toast ? <div className={`toast ${toast.tone}`}>{toast.message}</div> : null;
  return [show, node];
}

/**
 * The console's dropdown: shadcn's Select (Radix) styled with the tokens
 * above, never the browser's native list. `options` is `[value, label]` pairs.
 * Radix reserves the empty string, so "All …" options with `''` go through a
 * stand-in value. `onChange` receives `{ target: { value } }` so the same
 * field setters serve inputs and selects.
 */
const EMPTY = '__empty__';
export function Select({ value, onChange, options, size, className = '', disabled, label }) {
  const stop = (event) => event.stopPropagation();
  return (
    <SelectPrimitive.Root
      value={value == null || value === '' ? EMPTY : String(value)}
      onValueChange={(next) => onChange({ target: { value: next === EMPTY ? '' : next } })}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger className={`select-trigger ${size === 'sm' ? 'sm' : ''} ${className}`} aria-label={label} onClick={stop}>
        <SelectPrimitive.Value />
        <SelectPrimitive.Icon asChild><Icon name="chevronDown" size={14} className="select-chevron" /></SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        {/* Clicks inside bubble through React to the trigger's parents (a clickable row); Escape must close only the list, not the drawer behind it. */}
        <SelectPrimitive.Content className="select-content" position="popper" sideOffset={4} onClick={stop} onEscapeKeyDown={stop}>
          <SelectPrimitive.Viewport className="select-viewport">
            {options.map(([optionValue, optionLabel]) => (
              <SelectPrimitive.Item key={String(optionValue)} value={optionValue === '' ? EMPTY : String(optionValue)} className="select-item">
                <SelectPrimitive.ItemText>{optionLabel}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="select-check"><Icon name="check" size={14} /></SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export function OwnerSelect({ value, onChange, assignees, allowNone = true }) {
  const options = [...(allowNone ? [['', 'Unassigned']] : []), ...assignees.map((a) => [a.user_id, a.full_name || a.email])];
  return <Select value={value} onChange={(e) => onChange(e.target.value || null)} options={options} label="Owner" />;
}
